import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ManageHallOfFame from '../components/ManageHallOfFame';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportStats, setReportStats] = useState(null);
  const [accountTypeChanges, setAccountTypeChanges] = useState([]);
  const [scammerAccessRequests, setScammerAccessRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('approvals'); // 'approvals', 'reviews', 'approved-reviews', 'reports', 'account-changes', 'scammer-access', 'users', 'partners', 'chat' or 'hall-of-fame'
  const [giftThemeModal, setGiftThemeModal] = useState({ show: false, userId: null, username: '', userThemes: null, selectedThemeId: null, loading: false });
  const [availableThemes, setAvailableThemes] = useState([]);
  const [chatData, setChatData] = useState(null);
  const [grantCreditsModal, setGrantCreditsModal] = useState({ show: false, userId: null, username: '', credits: '', note: '', loading: false });
  const [grantPointsModal, setGrantPointsModal] = useState({ show: false, userId: null, username: '', points: '', note: '', loading: false });
  const [trustedBadgeModal, setTrustedBadgeModal] = useState({ show: false, userId: null, username: '', hasBadge: false, loading: false });
  const [banUserModal, setBanUserModal] = useState({ show: false, userId: null, username: '', banReason: '', loading: false });
  const [supporterTierModal, setSupporterTierModal] = useState({ show: false, userId: null, username: '', currentTier: null, loading: false });
  
  // Partners state
  const [partners, setPartners] = useState([]);
  const [partnerModal, setPartnerModal] = useState({
    show: false,
    mode: 'create', // 'create' or 'edit'
    partnerId: null,
    name: '',
    description: '',
    logoFile: null,
    logoPreview: '',
    links: [],
    order: 0,
    active: true,
    loading: false
  });

  // Helper function to ensure URL has proper protocol
  const ensureProtocol = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  useEffect(() => {
    fetchStats();
    fetchPendingApprovals();
    fetchPendingReviews();
    fetchReports();
    fetchReportStats();
    fetchAccountTypeChanges();
    fetchScammerAccessRequests();
    fetchAvailableThemes();
    fetchPartners();
  }, []);

  useEffect(() => {
    if (activeTab === 'chat' && !chatData) {
      fetchChatData();
    }
  }, [activeTab]);

  const fetchChatData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/chat/admin/data', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatData(response.data);
    } catch (err) {
      console.error('Error fetching chat data:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get('/api/admin/pending-approvals');
      setPendingApprovals(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/reviews/admin/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingReviews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching pending reviews:', err);
    }
  };

  const fetchApprovedReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/reviews/target/Game/all?includeAll=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const approved = Array.isArray(response.data) ? response.data.filter(r => r.status === 'approved') : [];
      setApprovedReviews(approved);
    } catch (err) {
      console.error('Error fetching approved reviews:', err);
    }
  };

  const handleApprove = async (approvalId) => {
    if (!window.confirm('Are you sure you want to approve this request?')) return;

    try {
      await axios.post(`/api/admin/approve/${approvalId}`);
      setMessage({ type: 'success', text: 'Request approved successfully!' });
      fetchPendingApprovals();
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to approve request' });
    }
  };

  const handleReject = async (approvalId) => {
    const reviewNote = window.prompt('Enter rejection reason (optional):');
    if (reviewNote === null) return; // User cancelled

    try {
      await axios.post(`/api/admin/reject/${approvalId}`, { reviewNote });
      setMessage({ type: 'success', text: 'Request rejected successfully!' });
      fetchPendingApprovals();
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reject request' });
    }
  };

  const handleApproveReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to approve this review?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/reviews/admin/approve/${reviewId}`,
        { adminNote: '' },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setMessage({ type: 'success', text: 'Review approved successfully!' });
      fetchPendingReviews();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to approve review' });
    }
  };

  const handleRejectReview = async (reviewId) => {
    const adminNote = window.prompt('Enter rejection reason (optional):');
    if (adminNote === null) return; // User cancelled

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/reviews/admin/reject/${reviewId}`,
        { adminNote },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setMessage({ type: 'success', text: 'Review rejected successfully!' });
      fetchPendingReviews();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reject review' });
    }
  };

  const handleFeatureReview = async (reviewId, currentlyFeatured) => {
    const action = currentlyFeatured ? 'unfeature' : 'feature';
    if (!window.confirm(`Are you sure you want to ${action} this review?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/reviews/admin/feature/${reviewId}`,
        { featured: !currentlyFeatured },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setMessage({ 
        type: 'success', 
        text: `Review ${currentlyFeatured ? 'unfeatured' : 'featured'} successfully!${!currentlyFeatured ? ' +100 points awarded to user!' : ''}` 
      });
      fetchPendingReviews();
      fetchApprovedReviews();
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to ${action} review` });
    }
  };

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/reports?status=pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data || []);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  const fetchReportStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/reports/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportStats(response.data);
    } catch (err) {
      console.error('Error fetching report stats:', err);
    }
  };

  const fetchAccountTypeChanges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/account-type-changes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountTypeChanges(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching account type changes:', err);
    }
  };

  const handleApproveAccountTypeChange = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this account type change?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/admin/account-type-changes/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Account type change approved successfully!' });
      fetchAccountTypeChanges();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to approve request' });
    }
  };

  const handleRejectAccountTypeChange = async (requestId) => {
    const adminNote = window.prompt('Enter rejection reason (optional):');
    if (adminNote === null) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/admin/account-type-changes/${requestId}/reject`, 
        { adminNote },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessage({ type: 'success', text: 'Account type change rejected!' });
      fetchAccountTypeChanges();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reject request' });
    }
  };

  // Scammer Access Request Functions
  const fetchScammerAccessRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/scammer-access-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScammerAccessRequests(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching scammer access requests:', err);
    }
  };

  const handleReviewScammerAccess = async (requestId, action) => {
    let confirmMessage = action === 'approve' 
      ? 'Are you sure you want to grant access to the scammers database?'
      : 'Are you sure you want to reject this access request?';
    
    if (!window.confirm(confirmMessage)) return;

    let rejectionReason = null;
    if (action === 'reject') {
      rejectionReason = window.prompt('Please provide a reason for rejection (this will be visible to the user):');
      if (rejectionReason === null) return; // User cancelled
    }

    try {
      const token = localStorage.getItem('token');
      const requestBody = { action };
      if (action === 'reject' && rejectionReason) {
        requestBody.rejectionReason = rejectionReason;
      }
      
      await axios.post(`/api/admin/scammer-access-requests/${requestId}/review`, 
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessage({ 
        type: 'success', 
        text: `Scammer access request ${action}d successfully!` 
      });
      // Remove the reviewed request from the UI immediately
      setScammerAccessRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to review request' 
      });
    }
  };

  const fetchAvailableThemes = async () => {
    try {
      const response = await axios.get('/api/shop/items');
      // Extract just the theme items from the shop response
      setAvailableThemes(response.data.data.items || []);
    } catch (err) {
      console.error('Error fetching available themes:', err);
      // Fallback to hardcoded themes if API fails
      setAvailableThemes([
        {
          id: 'cat-premium',
          name: 'Cat Paradise (Light)',
          description: 'An exclusive premium light theme with elegant cats, glassmorphism effects, and a clean sophisticated design. Features an interactive cat with eyes that follow your cursor! Donators only.',
          preview: '🐱',
          donatorsOnly: true
        },
        {
          id: 'cat-premium-dark',
          name: 'Cat Paradise (Dark)',
          description: 'An exclusive premium dark theme with elegant cats, glowing effects, and a sophisticated design. Features an interactive cat with eyes that follow your cursor! Donators only.',
          preview: '🐈‍⬛',
          donatorsOnly: true
        }
      ]);
    }
  };

  const fetchAllUsers = async (page = 1, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/users', {
        params: { page, limit: 20, search },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data.users);
      setUsersTotalPages(response.data.data.totalPages);
      setUsersPage(page);
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    }
  };

  const handleGiftTheme = async (userId, username) => {
    // Fetch user's current themes
    setGiftThemeModal({ show: true, userId, username, userThemes: null, selectedThemeId: null, loading: true });
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/admin/users/${userId}/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGiftThemeModal(prev => ({ 
        ...prev, 
        userThemes: response.data.data, 
        loading: false 
      }));
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: 'Failed to fetch user themes' 
      });
      setGiftThemeModal({ show: false, userId: null, username: '', userThemes: null, selectedThemeId: null, loading: false });
    }
  };

  const handleConfirmGiftTheme = async () => {
    const { userId, selectedThemeId } = giftThemeModal;
    
    if (!selectedThemeId) {
      setMessage({ type: 'error', text: 'Please select a theme' });
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/admin/users/${userId}/gift-theme`, 
        { themeId: selectedThemeId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage({ 
        type: 'success', 
        text: response.data.message 
      });
      
      // Close modal and refresh user data
      setGiftThemeModal({ show: false, userId: null, username: '', userThemes: null, selectedThemeId: null, loading: false });
      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to gift theme' 
      });
    }
  };

  const handleGrantCredits = (userId, username) => {
    setGrantCreditsModal({ 
      show: true, 
      userId, 
      username, 
      credits: '', 
      note: '', 
      loading: false 
    });
  };

  const handleGrantPoints = (userId, username) => {
    setGrantPointsModal({ 
      show: true, 
      userId, 
      username, 
      points: '', 
      note: '', 
      loading: false 
    });
  };

  const handleToggleTrustedBadge = (userId, username, hasBadge) => {
    setTrustedBadgeModal({
      show: true,
      userId,
      username,
      hasBadge,
      loading: false
    });
  };

  const handleBanUser = (userId, username) => {
    setBanUserModal({
      show: true,
      userId,
      username,
      banReason: '',
      loading: false
    });
  };

  const handleSetSupporterTier = (userId, username, currentTier) => {
    setSupporterTierModal({
      show: true,
      userId,
      username,
      currentTier,
      loading: false
    });
  };

  const submitSetSupporterTier = async (newTier) => {
    const { userId, username } = supporterTierModal;

    setSupporterTierModal(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/admin/users/${userId}/supporter-tier`,
        { tier: newTier },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setMessage({
        type: 'success',
        text: response.data.message
      });

      setSupporterTierModal({
        show: false,
        userId: null,
        username: '',
        currentTier: null,
        loading: false
      });

      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      console.error('Error setting supporter tier:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to set supporter tier'
      });
      setSupporterTierModal(prev => ({ ...prev, loading: false }));
    }
  };

  const submitBanUser = async () => {
    const { userId, username, banReason } = banUserModal;
    
    if (!banReason || banReason.trim().length < 10) {
      setMessage({ type: 'error', text: 'Ban reason must be at least 10 characters' });
      return;
    }

    if (!window.confirm(
      `⚠️ WARNING: This will PERMANENTLY BAN ${username} and DELETE ALL their data!\n\n` +
      `This action cannot be undone. Are you absolutely sure?`
    )) {
      return;
    }

    setBanUserModal(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/admin/users/${userId}/ban`,
        { banReason: banReason.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setMessage({ 
        type: 'success', 
        text: response.data.message || `${username} has been permanently banned` 
      });
      
      setBanUserModal({ 
        show: false, 
        userId: null, 
        username: '', 
        banReason: '', 
        loading: false 
      });
      
      // Refresh user list
      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      console.error('Error banning user:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to ban user' 
      });
      setBanUserModal(prev => ({ ...prev, loading: false }));
    }
  };

  const submitToggleTrustedBadge = async () => {
    const { userId, username, hasBadge } = trustedBadgeModal;
    
    if (!window.confirm(
      hasBadge 
        ? `Are you sure you want to REVOKE the Trusted Giveaway Host badge from ${username}?`
        : `Are you sure you want to GRANT the Trusted Giveaway Host badge to ${username}?`
    )) {
      return;
    }

    setTrustedBadgeModal(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/admin/users/${userId}/trusted-giveaway-host`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setMessage({ 
        type: 'success', 
        text: response.data.message || `Trusted badge ${hasBadge ? 'revoked from' : 'granted to'} ${username}!` 
      });
      
      setTrustedBadgeModal({ 
        show: false, 
        userId: null, 
        username: '', 
        hasBadge: false, 
        loading: false 
      });
      
      // Refresh user list to show updated badge status
      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      console.error('Error toggling trusted badge:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to toggle trusted badge' 
      });
      setTrustedBadgeModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleConfirmGrantCredits = async () => {
    const { userId, credits, note } = grantCreditsModal;
    
    if (!credits || credits <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid credit amount' });
      return;
    }
    
    setGrantCreditsModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/admin/users/${userId}/grant-credits`, 
        { credits: parseInt(credits), note },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage({ 
        type: 'success', 
        text: response.data.message 
      });
      
      // Close modal and refresh user data
      setGrantCreditsModal({ 
        show: false, 
        userId: null, 
        username: '', 
        credits: '', 
        note: '', 
        loading: false 
      });
      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to grant credits' 
      });
      setGrantCreditsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleConfirmGrantPoints = async () => {
    const { userId, points, note } = grantPointsModal;
    
    if (!points || points <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid points amount' });
      return;
    }
    
    setGrantPointsModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/admin/users/${userId}/grant-points`, 
        { points: parseInt(points), note },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage({ 
        type: 'success', 
        text: response.data.message || `Successfully granted ${points} points!`
      });
      
      setGrantPointsModal({ 
        show: false, 
        userId: null, 
        username: '', 
        points: '', 
        note: '', 
        loading: false 
      });
      fetchAllUsers(usersPage, usersSearch);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to grant points' 
      });
      setGrantPointsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getAccountTypeEmoji = (accountType) => {
    const emojiMap = {
      'regular': '🎮',
      'indie_dev': '👨‍💻',
      'content_creator': '🎬',
      'steam_curator': '🎯',
      'admin': '👑'
    };
    return emojiMap[accountType] || '🎮';
  };

  const getAccountTypeDisplay = (accountType) => {
    const displayMap = {
      'regular': 'Regular User (Gamer)',
      'indie_dev': 'Indie Developer',
      'content_creator': 'Content Creator',
      'steam_curator': 'Steam Curator',
      'admin': 'Admin'
    };
    return displayMap[accountType] || 'Regular User';
  };

  const getSupporterTierEmoji = (tier) => {
    const emojiMap = {
      'bronze': '🥉',
      'silver': '🥈',
      'gold': '🥇',
      'platinum': '💎'
    };
    return emojiMap[tier] || null;
  };

  const getSupporterTierDisplay = (tier) => {
    const displayMap = {
      'bronze': 'Bronze Supporter',
      'silver': 'Silver Supporter',
      'gold': 'Gold Supporter',
      'platinum': 'Platinum Supporter'
    };
    return displayMap[tier] || null;
  };

  const handleAcceptReport = async (reportId) => {
    const reviewNote = window.prompt('Enter review note (optional):');
    if (reviewNote === null) return;

    if (!window.confirm('This will DELETE the giveaway and give the host a WARNING. Are you sure?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/reports/${reportId}/accept`, 
        { reviewNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const warningMsg = response.data.hostBanned 
        ? 'Report accepted! Giveaway deleted and host BANNED (3 warnings reached).' 
        : `Report accepted! Giveaway deleted. Host now has ${response.data.hostWarnings} warning(s).`;
      
      setMessage({ type: 'success', text: warningMsg });
      fetchReports();
      fetchReportStats();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to accept report' });
    }
  };

  const handleRejectReport = async (reportId) => {
    const reviewNote = window.prompt('Enter rejection reason:');
    if (reviewNote === null) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/reports/${reportId}/reject`, 
        { reviewNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Report rejected.' });
      fetchReports();
      fetchReportStats();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reject report' });
    }
  };

  // Partner Management Functions
  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/partners/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  };

  const openPartnerModal = (mode, partner = null) => {
    if (mode === 'edit' && partner) {
      setPartnerModal({
        show: true,
        mode: 'edit',
        partnerId: partner._id,
        name: partner.name,
        description: partner.description,
        logoFile: null,
        logoPreview: partner.logo,
        links: partner.links || [],
        order: partner.order,
        active: partner.active,
        loading: false
      });
    } else {
      setPartnerModal({
        show: true,
        mode: 'create',
        partnerId: null,
        name: '',
        description: '',
        logoFile: null,
        logoPreview: '',
        links: [],
        order: 0,
        active: true,
        loading: false
      });
    }
  };

  const closePartnerModal = () => {
    setPartnerModal({
      show: false,
      mode: 'create',
      partnerId: null,
      name: '',
      description: '',
      logoFile: null,
      logoPreview: '',
      links: [],
      order: 0,
      active: true,
      loading: false
    });
  };

  const handlePartnerSubmit = async () => {
    const { mode, partnerId, name, description, logoFile, links, order, active } = partnerModal;

    if (!name || !description) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    if (mode === 'create' && !logoFile) {
      setMessage({ type: 'error', text: 'Please upload a logo image' });
      return;
    }

    setPartnerModal(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('links', JSON.stringify(links));
      formData.append('order', order);
      formData.append('active', active);
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      if (mode === 'create') {
        await axios.post('/api/partners', formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setMessage({ type: 'success', text: 'Partner created successfully!' });
      } else {
        await axios.put(`/api/partners/${partnerId}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setMessage({ type: 'success', text: 'Partner updated successfully!' });
      }

      closePartnerModal();
      fetchPartners();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save partner' });
    } finally {
      setPartnerModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Partner deleted successfully!' });
      fetchPartners();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete partner' });
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image must be less than 5MB' });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPartnerModal(prev => ({
          ...prev,
          logoFile: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddLink = () => {
    setPartnerModal(prev => ({
      ...prev,
      links: [...prev.links, { title: '', url: '' }]
    }));
  };

  const handleRemoveLink = (index) => {
    setPartnerModal(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const handleLinkChange = (index, field, value) => {
    setPartnerModal(prev => ({
      ...prev,
      links: prev.links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  // Chat Management Functions
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const roomData = {
      name: formData.get('name'),
      slug: formData.get('slug'),
      description: formData.get('description'),
      type: formData.get('type'),
      allowedRoles: formData.get('allowedRoles')?.split(',').map(r => r.trim()).filter(Boolean) || []
    };

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/chat/rooms', roomData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Room created successfully!' });
      e.target.reset();
      // Refetch chat data
      if (activeTab === 'chat') {
        const response = await axios.get('/api/chat/admin/data', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChatData(response.data);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to create room' });
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to deactivate this room?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/chat/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Room deactivated successfully!' });
      // Refetch chat data
      if (activeTab === 'chat') {
        const response = await axios.get('/api/chat/admin/data', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChatData(response.data);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to deactivate room' });
    }
  };

  const getFilteredApprovals = () => {
    if (filter === 'all') return pendingApprovals;
    if (filter === 'community') return pendingApprovals.filter(a => a.type === 'community_card');
    if (filter === 'games') return pendingApprovals.filter(a => a.type === 'indie_game');
    return pendingApprovals;
  };

  const renderOriginalData = (originalData, type) => {
    const isCard = type === 'community_card';
    
    return (
      <div className="original-data-preview">
        {isCard ? (
          <div className="approval-content">
            <div className="approval-header">
              {originalData.avatar && (
                <img 
                  src={`/api/media/${originalData.avatar}`} 
                  alt="Avatar" 
                  className="approval-avatar"
                  style={{ width: '50px', height: '50px', borderRadius: '50%', marginRight: '10px' }}
                />
              )}
              <div>
                <h4 style={{ margin: '0', fontSize: '16px' }}>{originalData.name}</h4>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>{originalData.about}</p>
              </div>
            </div>
            
            {originalData.socialLinks && originalData.socialLinks.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Social Links:</h5>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {originalData.socialLinks.map((link, idx) => (
                    <li key={idx} style={{ fontSize: '12px' }}>
                      <strong>{link.platform}:</strong>{' '}
                      <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                        {link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {originalData.donationLinks && originalData.donationLinks.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Donation Links:</h5>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {originalData.donationLinks.map((link, idx) => (
                    <li key={idx} style={{ fontSize: '12px' }}>
                      <strong>{link.platform}:</strong>{' '}
                      <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                        {link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {originalData.linktree && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Linktree:</h5>
                <a href={originalData.linktree} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                  {originalData.linktree}
                </a>
              </div>
            )}
            
            {originalData.hashtags && originalData.hashtags.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Hashtags:</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {originalData.hashtags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: '#95a5a6',
                        color: 'white',
                        padding: '3px 6px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="approval-content">
            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>{originalData.name}</h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>{originalData.description}</p>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
              <strong>Price:</strong> {originalData.price}
            </p>
            
            {originalData.storeLinks && originalData.storeLinks.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Store Links:</h5>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {originalData.storeLinks.map((link, idx) => (
                    <li key={idx} style={{ fontSize: '12px' }}>
                      <strong>{link.platform}:</strong>{' '}
                      <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                        {link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {originalData.media && originalData.media.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Media Files:</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {originalData.media.map((media, idx) => (
                    <div key={idx} style={{ fontSize: '11px', color: '#666' }}>
                      {media.filename}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {originalData.hashtags && originalData.hashtags.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <h5 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>Hashtags:</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {originalData.hashtags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: '#95a5a6',
                        color: 'white',
                        padding: '3px 6px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderApprovalCard = (approval) => {
    const data = approval.data;
    const isCard = approval.type === 'community_card';
    const actionLabel = approval.action.charAt(0).toUpperCase() + approval.action.slice(1);

    return (
      <div key={approval._id} className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
          <div>
            <h3 style={{ marginBottom: '5px' }}>
              {actionLabel} {isCard ? 'Community Card' : 'Indie Game'}
            </h3>
            <p style={{ fontSize: '12px', color: '#999' }}>
              By: {approval.userId?.username} ({approval.userId?.email})
            </p>
            <p style={{ fontSize: '12px', color: '#999' }}>
              Submitted: {new Date(approval.createdAt).toLocaleDateString()}
            </p>
          </div>
          <span
            style={{
              padding: '5px 10px',
              borderRadius: '5px',
              fontSize: '12px',
              fontWeight: 'bold',
              background: approval.action === 'delete' ? '#fee' : '#efe',
              color: approval.action === 'delete' ? '#c00' : '#060'
            }}
          >
            {actionLabel}
          </span>
        </div>

        <div className="approval-preview">
          {isCard ? (
            <div className="approval-content">
              <div className="approval-header">
                {data.avatar && (
                  <img 
                    src={`/api/media/${data.avatar}`} 
                    alt="Avatar" 
                    className="approval-avatar"
                  />
                )}
                <div>
                  <h4>{data.name}</h4>
                  <p className="approval-about">{data.about}</p>
                </div>
              </div>
              
              {data.socialLinks && data.socialLinks.length > 0 && (
                <div className="approval-links">
                  <h5>Social Links:</h5>
                  <ul>
                    {data.socialLinks.map((link, idx) => (
                      <li key={idx}>
                        <strong>{link.platform}:</strong>{' '}
                        <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                          {link.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {data.donationLinks && data.donationLinks.length > 0 && (
                <div className="approval-links">
                  <h5>Donation Links:</h5>
                  <ul>
                    {data.donationLinks.map((link, idx) => (
                      <li key={idx}>
                        <strong>{link.platform}:</strong>{' '}
                        <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                          {link.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {data.linktree && (
                <div className="approval-links">
                  <h5>Linktree:</h5>
                  <a href={ensureProtocol(data.linktree)} target="_blank" rel="noopener noreferrer">
                    {data.linktree}
                  </a>
                </div>
              )}
              
              {data.hashtags && data.hashtags.length > 0 && (
                <div className="approval-hashtags">
                  <h5>Hashtags:</h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                    {data.hashtags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          background: '#667eea',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="approval-content">
              <h4>{data.name}</h4>
              <p className="approval-about">{data.description}</p>
              <p className="approval-price">
                <strong>Price:</strong> {data.price}
              </p>
              
              {data.storeLinks && data.storeLinks.length > 0 && (
                <div className="approval-links">
                  <h5>Store Links:</h5>
                  <ul>
                    {data.storeLinks.map((link, idx) => (
                      <li key={idx}>
                        <strong>{link.platform}:</strong>{' '}
                        <a href={ensureProtocol(link.url)} target="_blank" rel="noopener noreferrer">
                          {link.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {data.media && data.media.length > 0 && (
                <div className="approval-media">
                  <h5>Media Files:</h5>
                  <div className="media-gallery">
                    {data.media.map((media, idx) => (
                      <div key={idx} className="media-item">
                        {media.fileType.startsWith('image/') ? (
                          <img 
                            src={`/api/media/${media.fileId}`} 
                            alt={`Media ${idx + 1}`} 
                            className="media-image"
                          />
                        ) : (
                          <video 
                            src={`/api/media/${media.fileId}`} 
                            controls 
                            className="media-video"
                          >
                            Your browser does not support the video tag.
                          </video>
                        )}
                        <p className="media-filename">{media.filename}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {data.hashtags && data.hashtags.length > 0 && (
                <div className="approval-hashtags">
                  <h5>Hashtags:</h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                    {data.hashtags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          background: '#667eea',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {approval.action === 'update' && approval.originalData && (
          <details style={{ marginBottom: '15px' }}>
            <summary style={{ cursor: 'pointer', color: '#667eea', fontWeight: 'bold' }}>
              View Original Data
            </summary>
            <div style={{ background: '#fff9e6', padding: '15px', borderRadius: '5px', marginTop: '10px' }}>
              {renderOriginalData(approval.originalData, approval.type)}
            </div>
          </details>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => handleApprove(approval._id)}
            className="btn btn-success"
          >
            ✅ Approve
          </button>
          <button
            onClick={() => handleReject(approval._id)}
            className="btn btn-danger"
          >
            ❌ Reject
          </button>
        </div>
      </div>
    );
  };

  const renderReviewCard = (review) => {
    const targetName = review.targetId?.name || 'Unknown';
    const targetType = review.targetType === 'CommunityCard' ? 'Community Card' : 'Indie Game';

    return (
      <div key={review._id} className="card">
        <div style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
            <div>
              <h3 style={{ marginBottom: '5px' }}>
                Review for {targetType}: {targetName}
              </h3>
              <p style={{ fontSize: '12px', color: '#999' }}>
                By: {review.user?.username}
              </p>
              <p style={{ fontSize: '12px', color: '#999' }}>
                Submitted: {new Date(review.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div className="star-rating-display">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${star <= review.rating ? 'filled' : 'empty'}`}
                  style={{ fontSize: '20px' }}
                >
                  ★
                </span>
              ))}
              <span style={{ marginLeft: '8px', color: '#667eea', fontWeight: 'bold' }}>
                {review.rating}/5
              </span>
            </div>
          </div>

          <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
            <p style={{ color: '#333', lineHeight: '1.6', margin: 0 }}>
              {review.comment}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleApproveReview(review._id)}
            className="btn btn-success"
          >
            ✅ Approve Review
          </button>
          <button
            onClick={() => handleRejectReview(review._id)}
            className="btn btn-danger"
          >
            ❌ Reject Review
          </button>
          {review.status === 'approved' && (
            <button
              onClick={() => handleFeatureReview(review._id, review.featured)}
              className="btn btn-primary"
              style={{
                backgroundColor: review.featured ? '#f39c12' : '#667eea',
                borderColor: review.featured ? '#f39c12' : '#667eea'
              }}
            >
              {review.featured ? '⭐ Unfeatured' : '⭐ Feature Review'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading admin dashboard...</div>;
  }

  return (
    <div className="container">
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Admin Dashboard</h2>

      {message.text && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{stats.totalUsers}</h3>
            <p>Total Users</p>
          </div>
          <div className="stat-card">
            <h3>{stats.pendingApprovals}</h3>
            <p>Pending Approvals</p>
          </div>
          <div className="stat-card">
            <h3>{stats.approvedCommunityCards}</h3>
            <p>Approved Cards</p>
          </div>
          <div className="stat-card">
            <h3>{stats.approvedIndieGames}</h3>
            <p>Approved Games</p>
          </div>
          <div className="stat-card">
            <h3>{stats.totalCreditsDistributed}</h3>
            <p>API Credits Distributed</p>
          </div>
          <div className="stat-card">
            <h3>{pendingReviews.length}</h3>
            <p>Pending Reviews</p>
          </div>
          <div className="stat-card">
            <h3>{reports.length}</h3>
            <p>Pending Reports</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTab('approvals')}
          className={`btn ${activeTab === 'approvals' ? 'btn-primary' : 'btn-secondary'}`}
        >
          Pending Approvals ({pendingApprovals.length})
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`btn ${activeTab === 'reviews' ? 'btn-primary' : 'btn-secondary'}`}
        >
          Pending Reviews ({pendingReviews.length})
        </button>
        <button
          onClick={() => { setActiveTab('approved-reviews'); fetchApprovedReviews(); }}
          className={`btn ${activeTab === 'approved-reviews' ? 'btn-primary' : 'btn-secondary'}`}
        >
          ⭐ Manage Featured Reviews
        </button>
        <button
          onClick={() => setActiveTab('account-changes')}
          className={`btn ${activeTab === 'account-changes' ? 'btn-primary' : 'btn-secondary'}`}
        >
          🔄 Account Type Changes ({accountTypeChanges.length})
        </button>
        <button
          onClick={() => setActiveTab('scammer-access')}
          className={`btn ${activeTab === 'scammer-access' ? 'btn-primary' : 'btn-secondary'}`}
        >
          🛡️ Scammer Access ({scammerAccessRequests.filter(r => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`btn ${activeTab === 'reports' ? 'btn-primary' : 'btn-secondary'}`}
        >
          🚨 Reports ({reports.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('users');
            fetchAllUsers(1, usersSearch);
          }}
          className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
        >
          👥 All Users
        </button>
        <button
          onClick={() => setActiveTab('partners')}
          className={`btn ${activeTab === 'partners' ? 'btn-primary' : 'btn-secondary'}`}
        >
          🤝 Partners ({partners.length})
        </button>
        <button
          onClick={() => setActiveTab('hall-of-fame')}
          className={`btn ${activeTab === 'hall-of-fame' ? 'btn-primary' : 'btn-secondary'}`}
        >
          🏆 Hall of Fame
        </button>
      </div>

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
        <>
          {/* Filters */}
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setFilter('all')}
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ marginRight: '10px' }}
            >
              All ({pendingApprovals.length})
            </button>
            <button
              onClick={() => setFilter('community')}
              className={`btn ${filter === 'community' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ marginRight: '10px' }}
            >
              Community Cards ({pendingApprovals.filter(a => a.type === 'community_card').length})
            </button>
            <button
              onClick={() => setFilter('games')}
              className={`btn ${filter === 'games' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Indie Games ({pendingApprovals.filter(a => a.type === 'indie_game').length})
            </button>
          </div>

          {/* Pending Approvals */}
          <h3 style={{ color: 'white', marginBottom: '15px' }}>Pending Approvals</h3>
          
          {getFilteredApprovals().length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666' }}>
                No pending approvals at the moment. Great job! 🎉
              </p>
            </div>
          ) : (
            <div>
              {getFilteredApprovals().map(approval => renderApprovalCard(approval))}
            </div>
          )}
        </>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <>
          <h3 style={{ color: 'white', marginBottom: '15px' }}>Pending Reviews</h3>
          
          {pendingReviews.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666' }}>
                No pending reviews at the moment. Great job! 🎉
              </p>
            </div>
          ) : (
            <div>
              {pendingReviews.map(review => renderReviewCard(review))}
            </div>
          )}
        </>
      )}

      {/* Approved Reviews Tab - Feature Management */}
      {activeTab === 'approved-reviews' && (
        <>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>⭐ Manage Featured Reviews</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Feature reviews to highlight them on the Game Reviews page. Featured reviewers earn +100 bonus points!
          </p>
          
          {approvedReviews.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                No approved reviews yet.
              </p>
            </div>
          ) : (
            <div>
              {/* Show featured reviews first */}
              {approvedReviews
                .sort((a, b) => {
                  if (a.featured && !b.featured) return -1;
                  if (!a.featured && b.featured) return 1;
                  return new Date(b.createdAt) - new Date(a.createdAt);
                })
                .map(review => (
                  <div key={review._id} className="card" style={{ 
                    marginBottom: '20px',
                    border: review.featured ? '2px solid #f39c12' : 'var(--border-color)',
                    background: review.featured ? 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(243, 156, 18, 0.05) 100%)' : 'var(--card-bg)',
                    boxShadow: review.featured ? '0 0 20px rgba(243, 156, 18, 0.2)' : 'none'
                  }}>
                    {review.featured && (
                      <div style={{ 
                        backgroundColor: '#f39c12', 
                        color: '#fff', 
                        padding: '8px 16px', 
                        marginBottom: '15px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        display: 'inline-block'
                      }}>
                        ⭐ FEATURED REVIEW
                      </div>
                    )}
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <h3 style={{ marginBottom: '5px', color: 'var(--text-primary)' }}>
                            {review.targetId?.name || 'Unknown Game'}
                          </h3>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            By: {review.user?.username}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            Submitted: {new Date(review.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="star-rating-display">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={`star ${star <= review.rating ? 'filled' : 'empty'}`}
                              style={{ fontSize: '20px' }}
                            >
                              ★
                            </span>
                          ))}
                          <span style={{ marginLeft: '8px', color: '#667eea', fontWeight: 'bold' }}>
                            {review.rating}/5
                          </span>
                        </div>
                      </div>

                      <div style={{ background: 'var(--hover-bg)', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                        <p style={{ color: 'var(--text-primary)', lineHeight: '1.6', margin: 0 }}>
                          {review.comment}
                        </p>
                      </div>

                      {review.pros && review.pros.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#28a745' }}>Pros:</strong>
                          <ul style={{ marginTop: '5px', color: 'var(--text-secondary)' }}>
                            {review.pros.map((pro, idx) => (
                              <li key={idx}>+ {pro}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {review.cons && review.cons.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ color: '#dc3545' }}>Cons:</strong>
                          <ul style={{ marginTop: '5px', color: 'var(--text-secondary)' }}>
                            {review.cons.map((con, idx) => (
                              <li key={idx}>− {con}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleFeatureReview(review._id, review.featured)}
                        className="btn btn-primary"
                        style={{
                          backgroundColor: review.featured ? '#e67e22' : '#667eea',
                          borderColor: review.featured ? '#e67e22' : '#667eea',
                          color: '#fff'
                        }}
                      >
                        {review.featured ? '⭐ Remove from Featured' : '⭐ Set as Featured'}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      )}

      {/* Account Type Changes Tab */}
      {activeTab === 'account-changes' && (
        <>
          <h3 style={{ color: 'white', marginBottom: '15px' }}>🔄 Account Type Change Requests</h3>
          
          {accountTypeChanges.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666' }}>
                No pending account type change requests at the moment.
              </p>
            </div>
          ) : (
            <div>
              {accountTypeChanges.map(request => (
                <div key={request._id} className="card" style={{ marginBottom: '20px', border: '1px solid #2196F3' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {request.user?.username}
                          {request.user?.profile?.avatar?.url && (
                            <img 
                              src={request.user.profile.avatar.url} 
                              alt={request.user.username}
                              style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                        </h4>
                        <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>
                          Email: <strong>{request.user?.email}</strong>
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                          Requested on: {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: '#FFA726',
                        color: 'white'
                      }}>
                        PENDING
                      </span>
                    </div>

                    <div style={{ 
                      background: '#f0f9ff', 
                      padding: '15px', 
                      borderRadius: '8px', 
                      marginBottom: '15px',
                      border: '1px solid #bae6fd'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '20px', 
                        marginBottom: '12px',
                        fontSize: '16px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                            {getAccountTypeEmoji(request.currentAccountType)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                            {getAccountTypeDisplay(request.currentAccountType)}
                          </div>
                        </div>
                        <div style={{ fontSize: '24px', color: '#2196F3' }}>→</div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                            {getAccountTypeEmoji(request.requestedAccountType)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#2196F3', fontWeight: 'bold' }}>
                            {getAccountTypeDisplay(request.requestedAccountType)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#666' }}>
                        Reason for Change:
                      </p>
                      <p style={{ color: '#333', lineHeight: '1.6', margin: 0 }}>
                        {request.reason}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleApproveAccountTypeChange(request._id)}
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleRejectAccountTypeChange(request._id)}
                      className="btn btn-danger"
                      style={{ flex: 1 }}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Scammer Access Requests Tab */}
      {activeTab === 'scammer-access' && (
        <>
          <h3 style={{ color: 'white', marginBottom: '15px' }}>🛡️ Scammer Database Access Requests</h3>
          
          {scammerAccessRequests.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666' }}>
                No scammer access requests at the moment.
              </p>
            </div>
          ) : (
            <div>
              {scammerAccessRequests.map(request => (
                <div 
                  key={request._id} 
                  className="card" 
                  style={{ 
                    marginBottom: '20px', 
                    border: `1px solid ${
                      request.status === 'approved' ? '#4CAF50' : 
                      request.status === 'rejected' ? '#dc2626' : 
                      '#FFA726'
                    }`
                  }}
                >
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>
                          {request.userId?.username}
                        </h4>
                        <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>
                          Email: <strong>{request.userId?.email}</strong>
                        </p>
                        <p style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#666' }}>
                          Account Type: <strong>{request.userId?.accountType || 'regular'}</strong>
                        </p>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                          Requested on: {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: 
                          request.status === 'approved' ? '#4CAF50' :
                          request.status === 'rejected' ? '#dc2626' :
                          '#FFA726',
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {request.status}
                      </span>
                    </div>

                    {request.requestReason && (
                      <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#666' }}>
                          Reason for Access:
                        </p>
                        <p style={{ color: '#333', lineHeight: '1.6', margin: 0 }}>
                          {request.requestReason}
                        </p>
                      </div>
                    )}

                    {request.reviewedBy && request.reviewedAt && (
                      <div style={{ 
                        background: request.status === 'approved' ? '#f0fdf4' : '#fef2f2', 
                        padding: '12px', 
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#666',
                        marginBottom: request.status === 'rejected' && request.rejectionReason ? '12px' : '0'
                      }}>
                        Reviewed by <strong>{request.reviewedBy?.username}</strong> on{' '}
                        {new Date(request.reviewedAt).toLocaleDateString()}
                      </div>
                    )}

                    {request.status === 'rejected' && request.rejectionReason && (
                      <div style={{ 
                        background: '#fee2e2', 
                        padding: '15px', 
                        borderRadius: '8px',
                        border: '1px solid #fca5a5'
                      }}>
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#dc2626' }}>
                          Rejection Reason:
                        </p>
                        <p style={{ color: '#991b1b', lineHeight: '1.6', margin: 0 }}>
                          {request.rejectionReason}
                        </p>
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleReviewScammerAccess(request._id, 'approve')}
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                      >
                        ✅ Grant Access
                      </button>
                      <button
                        onClick={() => handleReviewScammerAccess(request._id, 'reject')}
                        className="btn btn-danger"
                        style={{ flex: 1 }}
                      >
                        ❌ Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <>
          <h3 style={{ color: 'white', marginBottom: '15px' }}>🚨 Giveaway Reports</h3>
          
          {/* Report Stats */}
          {reportStats && (
            <div style={{ marginBottom: '20px', padding: '20px', background: '#1a1a1a', borderRadius: '12px' }}>
              <h4 style={{ color: '#white', marginBottom: '15px' }}>Report Statistics</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFA726' }}>{reportStats.pendingReports}</div>
                  <div style={{ fontSize: '14px', color: '#888' }}>Pending</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>{reportStats.acceptedReports}</div>
                  <div style={{ fontSize: '14px', color: '#888' }}>Accepted</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F44336' }}>{reportStats.rejectedReports}</div>
                  <div style={{ fontSize: '14px', color: '#888' }}>Rejected</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#64B5F6' }}>{reportStats.totalReports}</div>
                  <div style={{ fontSize: '14px', color: '#888' }}>Total</div>
                </div>
              </div>
            </div>
          )}

          {reports.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666' }}>
                No pending reports at the moment. Great job! 🎉
              </p>
            </div>
          ) : (
            <div>
              {reports.map(report => (
                <div key={report._id} className="card" style={{ marginBottom: '20px', border: '1px solid #FF5722' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{report.giveawayTitle}</h4>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                          Hosted by: <strong>{report.giveawayHost.username}</strong>
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                          Reported by: <strong>{report.reporter.username}</strong> on {new Date(report.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: '#FF5722',
                        color: 'white'
                      }}>
                        {report.reason.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>

                    <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                      <p style={{ margin: '0 0 5px 0', fontSize: '13px', fontWeight: 'bold', color: '#666' }}>
                        Report Description:
                      </p>
                      <p style={{ color: '#333', lineHeight: '1.6', margin: 0 }}>
                        {report.description}
                      </p>
                    </div>

                    {report.giveaway && (
                      <div style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                        Giveaway ID: <code>{report.giveaway._id}</code>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleAcceptReport(report._id)}
                      className="btn btn-danger"
                      style={{ flex: 1 }}
                    >
                      ✅ Accept (Delete Giveaway & Warn Host)
                    </button>
                    <button
                      onClick={() => handleRejectReport(report._id)}
                      className="btn"
                      style={{ flex: 1, background: '#666', color: 'white' }}
                    >
                      ❌ Reject Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          <div style={{
            marginBottom: '30px',
            background: 'var(--card-bg)',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: 'var(--shadow)',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              color: 'var(--text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🔍 Search Users
            </h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by username or email..."
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    fetchAllUsers(1, usersSearch);
                  }
                }}
                style={{
                  flex: 1,
                  minWidth: '180px',
                  padding: '8px 12px',
                  border: `2px solid var(--input-border)`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'var(--input-bg)',
                  color: 'var(--input-text)'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--btn-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--input-border)'}
              />
              <button
                onClick={() => fetchAllUsers(1, usersSearch)}
                className="btn btn-primary"
                style={{ 
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--btn-primary)',
                  color: 'var(--text-light)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontWeight: '500',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = 'var(--btn-primary-hover)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'var(--btn-primary)'}
              >
                🔍 Search
              </button>
            </div>
          </div>

          {!users || users.length === 0 ? (
            <div className="card" style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--card-bg)',
              borderRadius: '12px',
              boxShadow: 'var(--shadow)',
              border: '1px solid var(--border-color)'
            }}>
              <p style={{ 
                margin: '0', 
                color: 'var(--text-secondary)',
                fontSize: '16px',
                fontStyle: 'italic'
              }}>
                {!users ? 'Click search to load users...' : 'No users found matching your search.'}
              </p>
            </div>
          ) : (
            <>
              <div style={{ 
                marginBottom: '20px', 
                color: 'var(--text-secondary)', 
                fontSize: '14px',
                textAlign: 'center',
                backgroundColor: 'var(--card-bg)',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                📊 Page {usersPage} of {usersTotalPages} • Showing {users.length} users
              </div>
              
              {users.map(user => (
                <div key={user._id} className="card" style={{ 
                  marginBottom: '20px', 
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  boxShadow: 'var(--shadow)',
                  transition: 'all 0.2s ease',
                  background: 'var(--card-bg)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'start', 
                    marginBottom: '15px',
                    padding: '20px 20px 0 20px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)'
                      }}>
                        {getAccountTypeEmoji(user.accountType)} {user.username}
                        {getSupporterTierEmoji(user.supporterTier) && (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: 'var(--btn-success)',
                            color: 'var(--text-light)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {getSupporterTierEmoji(user.supporterTier)} {getSupporterTierDisplay(user.supporterTier)}
                          </span>
                        )}
                        {user.isBanned && (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: 'var(--btn-danger)',
                            color: 'var(--text-light)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Banned
                          </span>
                        )}
                      </h4>
                      <p style={{ 
                        margin: '0 0 5px 0', 
                        fontSize: '14px', 
                        color: 'var(--text-secondary)',
                        fontWeight: '500'
                      }}>
                        📧 {user.email}
                      </p>
                      <p style={{ 
                        margin: '0', 
                        fontSize: '13px', 
                        color: 'var(--text-primary)',
                        backgroundColor: 'var(--hover-bg)',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        display: 'inline-block'
                      }}>
                        {getAccountTypeDisplay(user.accountType)}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedUser(selectedUser === user._id ? null : user._id)}
                      className="btn btn-secondary"
                      style={{ 
                        fontSize: '13px',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: 'var(--btn-secondary)',
                        color: 'var(--text-light)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = 'var(--btn-secondary-hover)'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'var(--btn-secondary)'}
                    >
                      {selectedUser === user._id ? '👆 Hide Details' : '👀 View Details'}
                    </button>
                  </div>

                  {selectedUser === user._id && (
                    <div style={{ 
                      background: 'var(--hover-bg)', 
                      padding: '20px', 
                      borderRadius: '0 0 12px 12px',
                      borderTop: '1px solid var(--border-color)'
                    }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '20px', 
                        marginBottom: '20px' 
                      }}>
                        <div style={{
                          background: 'var(--card-bg)',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: 'var(--shadow)'
                        }}>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '11px', 
                            color: 'var(--text-secondary)', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            📅 Joined
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-primary)'
                          }}>
                            {new Date(user.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div style={{
                          background: 'var(--card-bg)',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: 'var(--shadow)'
                        }}>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '11px', 
                            color: 'var(--text-secondary)', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            👥 Followers / Following
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-primary)'
                          }}>
                            {user.followerCount || 0} / {user.followingCount || 0}
                          </p>
                        </div>
                        <div style={{
                          background: 'var(--card-bg)',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: 'var(--shadow)'
                        }}>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '11px', 
                            color: 'var(--text-secondary)', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            💳 Credits Balance
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px',
                            fontWeight: '500',
                            color: (user.credits || 0) > 0 ? '#10b981' : 'var(--text-primary)'
                          }}>
                            {(user.credits || 0).toLocaleString()}
                          </p>
                        </div>
                        <div style={{
                          background: 'var(--card-bg)',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: 'var(--shadow)'
                        }}>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '11px', 
                            color: 'var(--text-secondary)', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            ⚠️ Warnings
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px', 
                            fontWeight: '500',
                            color: (user.warningCount || 0) > 0 ? 'var(--btn-danger)' : 'var(--btn-success)' 
                          }}>
                            {user.warningCount || 0}
                          </p>
                        </div>
                        <div style={{
                          background: 'var(--card-bg)',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: 'var(--shadow)'
                        }}>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            fontSize: '11px', 
                            color: 'var(--text-secondary)', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            🔒 Verified Accounts
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--text-primary)'
                          }}>
                            {user.verifiedAccountsPublic ? '🌐 Public' : '🔒 Private'}
                          </p>
                        </div>
                      </div>

                      {/* Social Accounts */}
                      <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ 
                          margin: '0 0 15px 0', 
                          fontSize: '16px', 
                          color: 'var(--text-primary)',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          🔗 Verified Social Accounts
                        </h5>
                        {user.socialAccounts?.steam?.verified && (
                          <div style={{ 
                            background: 'linear-gradient(135deg, #1b2838 0%, #2a475e 100%)', 
                            color: 'white', 
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            marginBottom: '10px', 
                            fontSize: '13px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            <strong>Steam:</strong>{' '}
                            <a 
                              href={`https://steamcommunity.com/profiles/${user.socialAccounts.steam.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#66c0f4',
                                textDecoration: 'none',
                                fontWeight: '500'
                              }}
                              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {user.socialAccounts.steam.id}
                            </a>
                            <br />
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#b8c7ce',
                              marginTop: '4px',
                              display: 'block'
                            }}>
                              ✅ Verified on: {new Date(user.socialAccounts.steam.verifiedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {user.socialAccounts?.discord?.verified && (
                          <div style={{ 
                            background: 'linear-gradient(135deg, #5865F2 0%, #7289da 100%)', 
                            color: 'white', 
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            marginBottom: '10px', 
                            fontSize: '13px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            <strong>Discord:</strong> {user.socialAccounts.discord.username}
                            <br />
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#e0e7ff',
                              marginTop: '4px',
                              display: 'block'
                            }}>
                              ✅ Verified on: {new Date(user.socialAccounts.discord.verifiedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {user.socialAccounts?.twitter?.verified && (
                          <div style={{ 
                            background: 'linear-gradient(135deg, #1DA1F2 0%, #42a5f5 100%)', 
                            color: 'white', 
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            marginBottom: '10px', 
                            fontSize: '13px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            <strong>Twitter:</strong>{' '}
                            <a 
                              href={`https://twitter.com/${user.socialAccounts.twitter.username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: 'white', 
                                textDecoration: 'underline',
                                fontWeight: '500'
                              }}
                            >
                              @{user.socialAccounts.twitter.username}
                            </a>
                            <br />
                            <span style={{ 
                              fontSize: '11px', 
                              color: '#e0f2fe',
                              marginTop: '4px',
                              display: 'block'
                            }}>
                              ✅ Verified on: {new Date(user.socialAccounts.twitter.verifiedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {!user.socialAccounts?.steam?.verified && 
                         !user.socialAccounts?.discord?.verified && 
                         !user.socialAccounts?.twitter?.verified && (
                          <p style={{ 
                            margin: '0', 
                            fontSize: '14px', 
                            color: 'var(--text-secondary)', 
                            fontStyle: 'italic',
                            textAlign: 'center',
                            padding: '20px',
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '8px',
                            border: '2px dashed var(--border-color)'
                          }}>
                            No verified social accounts
                          </p>
                        )}
                      </div>

                      {/* Quick Actions */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        flexWrap: 'wrap',
                        paddingTop: '15px',
                        borderTop: '1px solid var(--border-color)'
                      }}>
                        <button
                          onClick={() => window.open(`/profile/${user.username}`, '_blank')}
                          className="btn btn-primary"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'var(--btn-primary)',
                            color: 'var(--text-light)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = 'var(--btn-primary-hover)'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'var(--btn-primary)'}
                        >
                          👤 View Profile
                        </button>
                        <button
                          onClick={() => handleGiftTheme(user._id, user.username)}
                          className="btn btn-secondary"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'var(--btn-secondary)',
                            color: 'var(--text-light)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = 'var(--btn-secondary-hover)'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'var(--btn-secondary)'}
                        >
                          🎁 Gift Theme
                        </button>
                        <button
                          onClick={() => handleGrantCredits(user._id, user.username)}
                          className="btn btn-success"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: '#10b981',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          💳 Grant Credits
                        </button>
                        <button
                          onClick={() => handleGrantPoints(user._id, user.username)}
                          className="btn btn-success"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
                        >
                          ⭐ Grant Points
                        </button>
                        <button
                          onClick={() => handleToggleTrustedBadge(user._id, user.username, user.trustedGiveawayHost || false)}
                          className="btn"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '2px solid',
                            borderColor: user.trustedGiveawayHost ? '#22c55e' : '#f59e0b',
                            backgroundColor: user.trustedGiveawayHost ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            color: user.trustedGiveawayHost ? '#22c55e' : '#f59e0b',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '600'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = user.trustedGiveawayHost ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)';
                            e.target.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = user.trustedGiveawayHost ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {user.trustedGiveawayHost ? '✓ Trusted Host' : '🎁 Grant Trusted Badge'}
                        </button>
{console.log('Rendering supporter button for', user.username, user.supporterTier)}
                        <button
                          onClick={() => handleSetSupporterTier(user._id, user.username, user.supporterTier || null)}
                          className="btn"
                          style={{ 
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '2px solid',
                            borderColor: user.supporterTier ? '#06b6d4' : '#64748b',
                            backgroundColor: user.supporterTier ? 'rgba(6, 182, 212, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: user.supporterTier ? '#06b6d4' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontWeight: '600'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = user.supporterTier ? 'rgba(6, 182, 212, 0.2)' : 'rgba(100, 116, 139, 0.2)';
                            e.target.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = user.supporterTier ? 'rgba(6, 182, 212, 0.1)' : 'rgba(100, 116, 139, 0.1)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {user.supporterTier 
                            ? `✓ ${getSupporterTierEmoji(user.supporterTier)} ${getSupporterTierDisplay(user.supporterTier)}` 
                            : '🏆 Set Supporter Tier'}
                        </button>
                        {user.accountType !== 'admin' && !user.isBanned && (
                          <button
                            onClick={() => handleBanUser(user._id, user.username)}
                            className="btn btn-danger"
                            style={{ 
                              fontSize: '13px',
                              padding: '10px 20px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              fontWeight: '600'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#dc2626';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#ef4444';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🚫 Ban User
                          </button>
                        )}
                        {user.isBanned && (
                          <div style={{
                            fontSize: '13px',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '2px solid #ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            fontWeight: '600'
                          }}>
                            🚫 BANNED
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {usersTotalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '15px', 
                  marginTop: '30px',
                  padding: '20px',
                  background: 'var(--card-bg)',
                  borderRadius: '12px',
                  boxShadow: 'var(--shadow)',
                  border: '1px solid var(--border-color)'
                }}>
                  <button
                    onClick={() => fetchAllUsers(usersPage - 1, usersSearch)}
                    className="btn btn-secondary"
                    disabled={usersPage === 1}
                    style={{ 
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: usersPage === 1 ? 'var(--hover-bg)' : 'var(--btn-secondary)',
                      color: usersPage === 1 ? 'var(--text-secondary)' : 'var(--text-light)',
                      cursor: usersPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseOver={(e) => {
                      if (usersPage !== 1) e.target.style.backgroundColor = 'var(--btn-secondary-hover)';
                    }}
                    onMouseOut={(e) => {
                      if (usersPage !== 1) e.target.style.backgroundColor = 'var(--btn-secondary)';
                    }}
                  >
                    ← Previous
                  </button>
                  <span style={{ 
                    padding: '10px 20px', 
                    fontSize: '14px', 
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    fontWeight: '500'
                  }}>
                    📄 Page {usersPage} of {usersTotalPages}
                  </span>
                  <button
                    onClick={() => fetchAllUsers(usersPage + 1, usersSearch)}
                    className="btn btn-secondary"
                    disabled={usersPage === usersTotalPages}
                    style={{ 
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: usersPage === usersTotalPages ? 'var(--hover-bg)' : 'var(--btn-secondary)',
                      color: usersPage === usersTotalPages ? 'var(--text-secondary)' : 'var(--text-light)',
                      cursor: usersPage === usersTotalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseOver={(e) => {
                      if (usersPage !== usersTotalPages) e.target.style.backgroundColor = 'var(--btn-secondary-hover)';
                    }}
                    onMouseOut={(e) => {
                      if (usersPage !== usersTotalPages) e.target.style.backgroundColor = 'var(--btn-secondary)';
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'white', margin: '0' }}>Manage Partners</h3>
            <button
              onClick={() => openPartnerModal('create')}
              className="btn btn-primary"
              style={{ fontSize: '14px' }}
            >
              ➕ Add New Partner
            </button>
          </div>

          {partners.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#888' }}>No partners added yet</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '15px' }}>
              {partners.map((partner) => (
                <div
                  key={partner._id}
                  className="card"
                  style={{
                    padding: '20px',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'start'
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    flexShrink: 0,
                    backgroundColor: '#2a2a2a',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px',
                    border: '1px solid #444'
                  }}>
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="font-size: 32px;">🤝</div>';
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <h4 style={{ margin: '0', color: 'white', fontSize: '18px' }}>{partner.name}</h4>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: partner.active ? '#10b981' : '#6b7280',
                        color: 'white'
                      }}>
                        {partner.active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: '#3b82f6',
                        color: 'white'
                      }}>
                        Order: {partner.order}
                      </span>
                    </div>
                    
                    <p style={{ margin: '8px 0', color: '#aaa', fontSize: '14px' }}>
                      {partner.description}
                    </p>
                    
                    {partner.links && partner.links.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ color: '#888', fontSize: '12px', marginBottom: '6px' }}>Links:</p>
                        {partner.links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#8a2be2',
                              fontSize: '13px',
                              textDecoration: 'none',
                              display: 'block',
                              marginBottom: '4px'
                            }}
                          >
                            🔗 {link.title}
                          </a>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => openPartnerModal('edit', partner)}
                        className="btn btn-secondary"
                        style={{ fontSize: '13px', padding: '8px 16px' }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeletePartner(partner._id)}
                        className="btn"
                        style={{
                          fontSize: '13px',
                          padding: '8px 16px',
                          backgroundColor: '#dc2626',
                          color: 'white'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Partner Modal */}
      {partnerModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '2px solid #333'
          }}>
            <h3 style={{ color: 'white', marginBottom: '25px', fontSize: '24px' }}>
              {partnerModal.mode === 'create' ? '➕ Add New Partner' : '✏️ Edit Partner'}
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#aaa', marginBottom: '8px', fontSize: '14px' }}>
                Partner Name *
              </label>
              <input
                type="text"
                value={partnerModal.name}
                onChange={(e) => setPartnerModal(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Steam, Epic Games, etc."
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#aaa', marginBottom: '8px', fontSize: '14px' }}>
                Description *
              </label>
              <textarea
                value={partnerModal.description}
                onChange={(e) => setPartnerModal(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the partnership"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#aaa', marginBottom: '8px', fontSize: '14px' }}>
                Logo Image * {partnerModal.mode === 'edit' && '(Leave empty to keep current logo)'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              />
              <p style={{ color: '#666', fontSize: '12px', marginTop: '6px', marginBottom: '0' }}>
                Max size: 5MB. Supported formats: JPG, PNG, GIF, WebP
              </p>
              {partnerModal.logoPreview && (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={partnerModal.logoPreview}
                    alt="Preview"
                    style={{
                      maxWidth: '120px',
                      maxHeight: '120px',
                      objectFit: 'contain',
                      border: '1px solid #444',
                      borderRadius: '6px',
                      padding: '10px',
                      backgroundColor: '#2a2a2a'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ color: '#aaa', fontSize: '14px' }}>
                  Links (Optional)
                </label>
                <button
                  type="button"
                  onClick={handleAddLink}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  ➕ Add Link
                </button>
              </div>
              
              {partnerModal.links.length === 0 ? (
                <p style={{ color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
                  No links added. Click "Add Link" to add website, social media, or other links.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {partnerModal.links.map((link, index) => (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 2fr auto',
                      gap: '8px',
                      alignItems: 'start',
                      padding: '12px',
                      backgroundColor: '#222',
                      borderRadius: '6px',
                      border: '1px solid #333'
                    }}>
                      <div>
                        <input
                          type="text"
                          placeholder="Title (e.g., Website)"
                          value={link.title}
                          onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="URL (e.g., https://example.com)"
                          value={link.url}
                          onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveLink(index)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#aaa', marginBottom: '8px', fontSize: '14px' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={partnerModal.order}
                  onChange={(e) => setPartnerModal(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#aaa', marginBottom: '8px', fontSize: '14px' }}>
                  Status
                </label>
                <select
                  value={partnerModal.active}
                  onChange={(e) => setPartnerModal(prev => ({ ...prev, active: e.target.value === 'true' }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closePartnerModal}
                disabled={partnerModal.loading}
                className="btn btn-secondary"
                style={{ fontSize: '14px', padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                onClick={handlePartnerSubmit}
                disabled={partnerModal.loading}
                className="btn btn-primary"
                style={{ fontSize: '14px', padding: '10px 20px' }}
              >
                {partnerModal.loading ? 'Saving...' : (partnerModal.mode === 'create' ? 'Create Partner' : 'Update Partner')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gift Theme Modal */}
      {giftThemeModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{ color: 'var(--text-light)', marginBottom: '20px', fontSize: '24px' }}>
              🎁 Gift Theme to {giftThemeModal.username}
            </h3>

            {giftThemeModal.loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                Loading themes...
              </div>
            ) : giftThemeModal.userThemes ? (
              <>
                {/* Show owned themes */}
                <div style={{ 
                  marginBottom: '25px', 
                  padding: '15px', 
                  backgroundColor: 'var(--bg-primary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  <h4 style={{ color: 'var(--text-light)', marginBottom: '12px', fontSize: '16px' }}>
                    ✅ Currently Owned Themes ({giftThemeModal.userThemes.totalOwned})
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {giftThemeModal.userThemes.defaultThemes.map(theme => (
                      <span key={theme.id} style={{
                        padding: '6px 12px',
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4CAF50',
                        borderRadius: '6px',
                        fontSize: '13px',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                      }}>
                        {theme.preview} {theme.name}
                      </span>
                    ))}
                    {giftThemeModal.userThemes.ownedShopThemes.map(theme => (
                      <span key={theme.id} style={{
                        padding: '6px 12px',
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4CAF50',
                        borderRadius: '6px',
                        fontSize: '13px',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                      }}>
                        {theme.preview} {theme.name}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Active Theme: <span style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>
                      {giftThemeModal.userThemes.user.activeTheme}
                    </span>
                  </div>
                </div>

                {/* Available themes to gift */}
                <div>
                  <h4 style={{ color: 'var(--text-light)', marginBottom: '15px', fontSize: '16px' }}>
                    🎁 Select a Theme to Gift
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {giftThemeModal.userThemes.shopThemes.map(theme => (
                      <div
                        key={theme.id}
                        onClick={() => {
                          if (!theme.owned) {
                            setGiftThemeModal(prev => ({ ...prev, selectedThemeId: theme.id }));
                          }
                        }}
                        style={{
                          padding: '15px',
                          backgroundColor: theme.owned 
                            ? 'rgba(100, 100, 100, 0.3)' 
                            : giftThemeModal.selectedThemeId === theme.id
                              ? 'var(--primary-color)'
                              : 'var(--bg-primary)',
                          borderRadius: '8px',
                          border: `2px solid ${
                            theme.owned 
                              ? 'rgba(100, 100, 100, 0.5)' 
                              : giftThemeModal.selectedThemeId === theme.id
                                ? 'var(--primary-color)'
                                : 'var(--border-color)'
                          }`,
                          cursor: theme.owned ? 'not-allowed' : 'pointer',
                          opacity: theme.owned ? 0.5 : 1,
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                      >
                        {theme.owned && (
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            backgroundColor: 'rgba(76, 175, 80, 0.9)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            OWNED
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '32px' }}>{theme.preview}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              color: 'var(--text-light)', 
                              fontWeight: 'bold',
                              fontSize: '15px',
                              marginBottom: '4px'
                            }}>
                              {theme.name}
                              <span style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                backgroundColor: theme.rarity === 'legendary' ? '#9C27B0' : theme.rarity === 'rare' ? '#2196F3' : '#4CAF50',
                                color: 'white',
                                borderRadius: '10px',
                                fontSize: '10px',
                                textTransform: 'uppercase'
                              }}>
                                {theme.rarity}
                              </span>
                            </div>
                            <div style={{ 
                              color: 'var(--text-muted)', 
                              fontSize: '13px',
                              marginBottom: '6px'
                            }}>
                              {theme.description}
                            </div>
                            <div style={{ 
                              color: 'var(--text-muted)', 
                              fontSize: '12px'
                            }}>
                              💰 {theme.price} Activity Points
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  marginTop: '25px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setGiftThemeModal({ show: false, userId: null, username: '', userThemes: null, selectedThemeId: null, loading: false })}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-light)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = 'var(--bg-hover)'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'var(--bg-primary)'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmGiftTheme}
                    disabled={!giftThemeModal.selectedThemeId}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: giftThemeModal.selectedThemeId ? 'var(--btn-primary)' : '#555',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: giftThemeModal.selectedThemeId ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (giftThemeModal.selectedThemeId) {
                        e.target.style.backgroundColor = 'var(--btn-primary-hover)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (giftThemeModal.selectedThemeId) {
                        e.target.style.backgroundColor = 'var(--btn-primary)';
                      }
                    }}
                  >
                    🎁 Gift Theme
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Grant Credits Modal */}
      {grantCreditsModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{ color: 'var(--text-light)', marginBottom: '20px', fontSize: '24px' }}>
              💳 Grant Credits to {grantCreditsModal.username}
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'var(--text-light)', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Credit Amount *
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Enter credits to grant (e.g., 28448)"
                value={grantCreditsModal.credits}
                onChange={(e) => setGrantCreditsModal(prev => ({ ...prev, credits: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {/* Quick credit amounts */}
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                gap: '8px', 
                flexWrap: 'wrap' 
              }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '100%', marginBottom: '4px' }}>
                  Quick amounts:
                </span>
                {[
                  { label: '$1', credits: 28448 },
                  { label: '$3', credits: 112810 },
                  { label: '$5', credits: 197207 },
                  { label: '$10', credits: 408167 }
                ].map(({ label, credits }) => (
                  <button
                    key={label}
                    onClick={() => setGrantCreditsModal(prev => ({ ...prev, credits: credits.toString() }))}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                    }}
                  >
                    {label} ({credits.toLocaleString()})
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'var(--text-light)', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Note (Optional)
              </label>
              <textarea
                placeholder="Reason for granting credits (e.g., Payment via Buy Me a Coffee)"
                value={grantCreditsModal.note}
                onChange={(e) => setGrantCreditsModal(prev => ({ ...prev, note: e.target.value }))}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Action buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '25px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setGrantCreditsModal({ show: false, userId: null, username: '', credits: '', note: '', loading: false })}
                disabled={grantCreditsModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: grantCreditsModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: grantCreditsModal.loading ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!grantCreditsModal.loading) e.target.style.backgroundColor = 'var(--bg-hover)';
                }}
                onMouseOut={(e) => {
                  if (!grantCreditsModal.loading) e.target.style.backgroundColor = 'var(--bg-primary)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGrantCredits}
                disabled={!grantCreditsModal.credits || grantCreditsModal.credits <= 0 || grantCreditsModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (!grantCreditsModal.credits || grantCreditsModal.credits <= 0 || grantCreditsModal.loading) ? '#555' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!grantCreditsModal.credits || grantCreditsModal.credits <= 0 || grantCreditsModal.loading) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (grantCreditsModal.credits > 0 && !grantCreditsModal.loading) {
                    e.target.style.backgroundColor = '#059669';
                  }
                }}
                onMouseOut={(e) => {
                  if (grantCreditsModal.credits > 0 && !grantCreditsModal.loading) {
                    e.target.style.backgroundColor = '#10b981';
                  }
                }}
              >
                {grantCreditsModal.loading ? 'Granting...' : '💳 Grant Credits'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Points Modal */}
      {grantPointsModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            border: '2px solid #8b5cf6'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              color: 'var(--text-light)', 
              fontSize: '22px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ⭐ Grant Activity Points to {grantPointsModal.username}
            </h3>

            <div style={{
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid #8b5cf6',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                margin: '0', 
                color: 'var(--text-light)',
                fontSize: '13px',
                lineHeight: '1.6'
              }}>
                ℹ️ Activity points are awarded to users for participation. Points contribute to their leaderboard ranking and can be spent in the theme shop.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'var(--text-light)', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Points Amount
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Enter points to grant (e.g., 500)"
                value={grantPointsModal.points}
                onChange={(e) => setGrantPointsModal(prev => ({ ...prev, points: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {/* Quick point amounts */}
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                gap: '8px', 
                flexWrap: 'wrap' 
              }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '100%', marginBottom: '4px' }}>
                  Quick amounts:
                </span>
                {[100, 500, 1000, 5000, 10000].map((points) => (
                  <button
                    key={points}
                    onClick={() => setGrantPointsModal(prev => ({ ...prev, points: points.toString() }))}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(139, 92, 246, 0.2)',
                      color: '#8b5cf6',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'rgba(139, 92, 246, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                    }}
                  >
                    {points.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'var(--text-light)', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Note (Optional)
              </label>
              <textarea
                placeholder="Reason for granting points (e.g., Community contribution, Bug report)"
                value={grantPointsModal.note}
                onChange={(e) => setGrantPointsModal(prev => ({ ...prev, note: e.target.value }))}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Action buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '25px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setGrantPointsModal({ show: false, userId: null, username: '', points: '', note: '', loading: false })}
                disabled={grantPointsModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: grantPointsModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: grantPointsModal.loading ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!grantPointsModal.loading) e.target.style.backgroundColor = 'var(--bg-hover)';
                }}
                onMouseOut={(e) => {
                  if (!grantPointsModal.loading) e.target.style.backgroundColor = 'var(--bg-primary)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGrantPoints}
                disabled={!grantPointsModal.points || grantPointsModal.points <= 0 || grantPointsModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (!grantPointsModal.points || grantPointsModal.points <= 0 || grantPointsModal.loading) ? '#555' : '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!grantPointsModal.points || grantPointsModal.points <= 0 || grantPointsModal.loading) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (grantPointsModal.points > 0 && !grantPointsModal.loading) {
                    e.target.style.backgroundColor = '#7c3aed';
                  }
                }}
                onMouseOut={(e) => {
                  if (grantPointsModal.points > 0 && !grantPointsModal.loading) {
                    e.target.style.backgroundColor = '#8b5cf6';
                  }
                }}
              >
                {grantPointsModal.loading ? 'Granting...' : '⭐ Grant Points'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trusted Giveaway Host Badge Modal */}
      {trustedBadgeModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            border: `2px solid ${trustedBadgeModal.hasBadge ? '#22c55e' : '#f59e0b'}`
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              color: 'var(--text-light)', 
              fontSize: '22px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {trustedBadgeModal.hasBadge ? '❌ Revoke' : '🎁 Grant'} Trusted Giveaway Host Badge
            </h3>

            <div style={{
              backgroundColor: trustedBadgeModal.hasBadge ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              border: `2px solid ${trustedBadgeModal.hasBadge ? '#22c55e' : '#f59e0b'}`,
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <div style={{ 
                fontSize: '48px', 
                textAlign: 'center', 
                marginBottom: '15px' 
              }}>
                {trustedBadgeModal.hasBadge ? '✓' : '🎁'}
              </div>
              <div style={{ 
                color: 'var(--text-light)', 
                fontSize: '16px',
                textAlign: 'center',
                marginBottom: '10px'
              }}>
                <strong>{trustedBadgeModal.username}</strong>
              </div>
              <div style={{ 
                color: trustedBadgeModal.hasBadge ? '#22c55e' : '#f59e0b',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                {trustedBadgeModal.hasBadge 
                  ? '✓ Currently has Trusted Badge' 
                  : '⚠ Does not have Trusted Badge'}
              </div>
            </div>

            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                margin: '0 0 10px 0', 
                color: 'var(--text-light)',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                ℹ️ About the Trusted Badge:
              </p>
              <ul style={{ 
                margin: '0', 
                paddingLeft: '20px',
                color: 'var(--text-muted)',
                fontSize: '12px',
                lineHeight: '1.6'
              }}>
                <li>Displays on all giveaways hosted by this user</li>
                <li>Helps users identify verified, trustworthy hosts</li>
                <li>Can only be granted by AlonelyCat31</li>
                <li>Action will be logged with timestamp</li>
              </ul>
            </div>

            {/* Action buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '25px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setTrustedBadgeModal({ show: false, userId: null, username: '', hasBadge: false, loading: false })}
                disabled={trustedBadgeModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: trustedBadgeModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: trustedBadgeModal.loading ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!trustedBadgeModal.loading) e.target.style.backgroundColor = 'var(--bg-hover)';
                }}
                onMouseOut={(e) => {
                  if (!trustedBadgeModal.loading) e.target.style.backgroundColor = 'var(--bg-primary)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitToggleTrustedBadge}
                disabled={trustedBadgeModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: trustedBadgeModal.loading 
                    ? '#555' 
                    : trustedBadgeModal.hasBadge 
                      ? '#ef4444' 
                      : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: trustedBadgeModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (!trustedBadgeModal.loading) {
                    e.target.style.backgroundColor = trustedBadgeModal.hasBadge ? '#dc2626' : '#16a34a';
                  }
                }}
                onMouseOut={(e) => {
                  if (!trustedBadgeModal.loading) {
                    e.target.style.backgroundColor = trustedBadgeModal.hasBadge ? '#ef4444' : '#22c55e';
                  }
                }}
              >
                {trustedBadgeModal.loading 
                  ? 'Processing...' 
                  : trustedBadgeModal.hasBadge 
                    ? '❌ Revoke Badge' 
                    : '✓ Grant Badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supporter Tier Modal */}
      {supporterTierModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            border: '2px solid #06b6d4'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              color: 'var(--text-light)', 
              fontSize: '22px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              🏆 Set Supporter Tier
            </h3>

            <div style={{
              backgroundColor: 'rgba(6, 182, 212, 0.1)',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <div style={{ 
                color: 'var(--text-light)', 
                fontSize: '16px',
                textAlign: 'center',
                marginBottom: '10px'
              }}>
                <strong>{supporterTierModal.username}</strong>
              </div>
              <div style={{ 
                color: '#06b6d4',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Current Tier: {supporterTierModal.currentTier 
                  ? `${getSupporterTierEmoji(supporterTierModal.currentTier)} ${getSupporterTierDisplay(supporterTierModal.currentTier)}` 
                  : 'None'}
              </div>
            </div>

            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                margin: '0 0 10px 0', 
                color: 'var(--text-light)',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                ℹ️ Supporter Tier Perks:
              </p>
              <ul style={{ 
                margin: '0', 
                paddingLeft: '20px',
                color: 'var(--text-muted)',
                fontSize: '12px',
                lineHeight: '1.8'
              }}>
                <li>🥉 Bronze: 1.25x entries, 1.25x points</li>
                <li>🥈 Silver: 1.5x entries, 1.5x points</li>
                <li>🥇 Gold: 1.75x entries, 1.75x points</li>
                <li>💎 Platinum: 2x entries, 2x points</li>
              </ul>
            </div>

            {/* Tier Selection */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '12px', 
              marginBottom: '20px' 
            }}>
              {[ 
                { tier: 'bronze', emoji: '🥉', name: 'Bronze', color: '#cd7f32' },
                { tier: 'silver', emoji: '🥈', name: 'Silver', color: '#c0c0c0' },
                { tier: 'gold', emoji: '🥇', name: 'Gold', color: '#ffd700' },
                { tier: 'platinum', emoji: '💎', name: 'Platinum', color: '#e5e4e2' }
              ].map(({ tier, emoji, name, color }) => (
                <button
                  key={tier}
                  onClick={() => submitSetSupporterTier(tier)}
                  disabled={supporterTierModal.loading}
                  style={{
                    padding: '15px',
                    backgroundColor: supporterTierModal.loading 
                      ? '#555' 
                      : supporterTierModal.currentTier === tier 
                        ? 'rgba(6, 182, 212, 0.2)' 
                        : 'var(--bg-primary)',
                    border: `2px solid ${supporterTierModal.currentTier === tier ? '#06b6d4' : color}`,
                    borderRadius: '8px',
                    color: 'var(--text-light)',
                    cursor: supporterTierModal.loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => {
                    if (!supporterTierModal.loading) {
                      e.target.style.backgroundColor = 'rgba(6, 182, 212, 0.15)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!supporterTierModal.loading) {
                      e.target.style.backgroundColor = supporterTierModal.currentTier === tier 
                        ? 'rgba(6, 182, 212, 0.2)' 
                        : 'var(--bg-primary)';
                    }
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{emoji}</span>
                  <span>{name} Supporter</span>
                </button>
              ))}
            </div>

            {/* Revoke Button */}
            {supporterTierModal.currentTier && (
              <button
                onClick={() => submitSetSupporterTier(null)}
                disabled={supporterTierModal.loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: supporterTierModal.loading 
                    ? '#555' 
                    : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: supporterTierModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  marginBottom: '15px'
                }}
                onMouseOver={(e) => {
                  if (!supporterTierModal.loading) {
                    e.target.style.backgroundColor = '#dc2626';
                  }
                }}
                onMouseOut={(e) => {
                  if (!supporterTierModal.loading) {
                    e.target.style.backgroundColor = '#ef4444';
                  }
                }}
              >
                {supporterTierModal.loading ? 'Processing...' : '❌ Revoke Supporter Tier'}
              </button>
            )}

            {/* Action buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setSupporterTierModal({ show: false, userId: null, username: '', currentTier: null, loading: false })}
                disabled={supporterTierModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: supporterTierModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: supporterTierModal.loading ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!supporterTierModal.loading) e.target.style.backgroundColor = 'var(--bg-hover)';
                }}
                onMouseOut={(e) => {
                  if (!supporterTierModal.loading) e.target.style.backgroundColor = 'var(--bg-primary)';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {banUserModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            border: '3px solid #ef4444'
          }}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              color: '#ef4444', 
              fontSize: '24px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ⚠️ Ban User Permanently
            </h3>

            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '2px solid #ef4444',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '25px'
            }}>
              <div style={{ 
                fontSize: '48px', 
                textAlign: 'center', 
                marginBottom: '15px' 
              }}>
                🚫
              </div>
              <div style={{ 
                color: 'var(--text-light)', 
                fontSize: '18px',
                textAlign: 'center',
                marginBottom: '15px',
                fontWeight: '600'
              }}>
                {banUserModal.username}
              </div>
              <div style={{ 
                color: '#ef4444',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '600',
                lineHeight: '1.6'
              }}>
                ⚠️ WARNING: This action is PERMANENT and IRREVERSIBLE!
              </div>
            </div>

            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '2px solid #ef4444',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                margin: '0 0 10px 0', 
                color: '#ef4444',
                fontSize: '14px',
                fontWeight: '700'
              }}>
                ⚠️ This will IMMEDIATELY:
              </p>
              <ul style={{ 
                margin: '0', 
                paddingLeft: '20px',
                color: 'var(--text-light)',
                fontSize: '13px',
                lineHeight: '1.8'
              }}>
                <li>Mark the account as banned</li>
                <li>Delete ALL posts, comments, and activities</li>
                <li>Delete ALL giveaways created by this user</li>
                <li>Delete ALL giveaway entries</li>
                <li>Delete ALL achievements and rewards</li>
                <li>Delete ALL messages and conversations</li>
                <li>Remove user from all followers/following lists</li>
                <li>Block email and Twitter account from future registration/login</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                color: 'var(--text-light)', 
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Ban Reason (Required - min 10 characters) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                placeholder="Enter detailed reason for banning this user (e.g., spamming, harassment, violation of terms)"
                value={banUserModal.banReason}
                onChange={(e) => setBanUserModal(prev => ({ ...prev, banReason: e.target.value }))}
                rows="4"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{
                fontSize: '12px',
                color: banUserModal.banReason.length >= 10 ? '#10b981' : '#ef4444',
                marginTop: '5px'
              }}>
                {banUserModal.banReason.length} / 10 characters minimum
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '25px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setBanUserModal({ show: false, userId: null, username: '', banReason: '', loading: false })}
                disabled={banUserModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: banUserModal.loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: banUserModal.loading ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!banUserModal.loading) e.target.style.backgroundColor = 'var(--bg-hover)';
                }}
                onMouseOut={(e) => {
                  if (!banUserModal.loading) e.target.style.backgroundColor = 'var(--bg-primary)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitBanUser}
                disabled={banUserModal.banReason.trim().length < 10 || banUserModal.loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (banUserModal.banReason.trim().length < 10 || banUserModal.loading) ? '#555' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (banUserModal.banReason.trim().length < 10 || banUserModal.loading) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (banUserModal.banReason.trim().length >= 10 && !banUserModal.loading) {
                    e.target.style.backgroundColor = '#dc2626';
                  }
                }}
                onMouseOut={(e) => {
                  if (banUserModal.banReason.trim().length >= 10 && !banUserModal.loading) {
                    e.target.style.backgroundColor = '#ef4444';
                  }
                }}
              >
                {banUserModal.loading ? '⏳ Banning User...' : '🚫 PERMANENTLY BAN USER'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Management Tab */}
      {activeTab === 'chat' && chatData && (
        <>
          <h3 style={{ color: 'white', marginBottom: '15px' }}>💬 Chat Management</h3>

          {/* Chat Stats */}
          <div className="stats-grid" style={{ marginBottom: '30px' }}>
            <div className="stat-card">
              <h3>{chatData.stats.totalRooms}</h3>
              <p>Total Rooms</p>
            </div>
            <div className="stat-card">
              <h3>{chatData.stats.activeRooms}</h3>
              <p>Active Rooms</p>
            </div>
            <div className="stat-card">
              <h3>{chatData.stats.totalMessages}</h3>
              <p>Total Messages</p>
            </div>
            <div className="stat-card">
              <h3>{chatData.stats.messagesToday}</h3>
              <p>Messages Today</p>
            </div>
          </div>

          {/* Create Room Form */}
          <div className="card" style={{ marginBottom: '30px', border: '1px solid #2196F3' }}>
            <h4 style={{ color: '#2196F3', marginBottom: '15px' }}>Create New Room</h4>
            <form onSubmit={handleCreateRoom} style={{ display: 'grid', gap: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <input type="text" name="name" placeholder="Room Name" required className="form-control" />
                <input type="text" name="slug" placeholder="Slug (e.g. general-chat)" required className="form-control" />
              </div>
              <input type="text" name="description" placeholder="Description" required className="form-control" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <select name="type" className="form-control" required>
                  <option value="public">Public</option>
                  <option value="private">Private (Role Locked)</option>
                  <option value="paid">Paid (Coming Soon)</option>
                </select>
                <input type="text" name="allowedRoles" placeholder="Allowed Roles (comma separated, e.g. admin,moderator)" className="form-control" />
              </div>
              <button type="submit" className="btn btn-primary">Create Room</button>
            </form>
          </div>

          {/* Room List */}
          <h4 style={{ color: 'white', marginBottom: '15px' }}>Existing Rooms</h4>
          <div style={{ display: 'grid', gap: '15px' }}>
            {chatData.rooms.map(room => (
              <div key={room._id} className="card" style={{ 
                borderLeft: `4px solid ${room.isActive ? '#4CAF50' : '#F44336'}`,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <div>
                  <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                    #{room.name} 
                    {room.isSystem && <span style={{ fontSize: '0.8em', background: '#2196F3', color: 'white', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>SYSTEM</span>}
                    {!room.isActive && <span style={{ fontSize: '0.8em', background: '#F44336', color: 'white', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>INACTIVE</span>}
                  </h4>
                  <p style={{ margin: '0 0 5px 0', color: '#666' }}>{room.description}</p>
                  <div style={{ fontSize: '0.9em', color: '#888' }}>
                    Type: <strong>{room.type}</strong> | Slug: <code>{room.slug}</code>
                  </div>
                </div>
                <div>
                  {room.isActive && !room.isSystem && (
                    <button 
                      onClick={() => handleDeleteRoom(room._id)}
                      className="btn" 
                      style={{ backgroundColor: '#F44336', color: 'white' }}
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Hall of Fame Tab */}
      {activeTab === 'hall-of-fame' && (
        <ManageHallOfFame />
      )}
    </div>
  );
};

export default AdminDashboard;
